import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router'
import { Suspense, lazy, useEffect } from 'react'
import clsx from 'clsx'
import { useApp } from '@/lib/store'
import { Spinner } from './bits'

const GuideDock = lazy(() => import('@/guide/GuideDock'))

const NAV = [
  { to: '/map', label: 'Atlas' },
  { to: '/inside', label: 'Inside' },
  { to: '/voices', label: 'Voices' },
  { to: '/learn', label: 'Learn' },
  { to: '/portal', label: 'For Companies' },
]

export function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2 font-semibold tracking-tight" aria-label="Glasshouse home">
      <svg viewBox="0 0 64 64" width="26" height="26" aria-hidden>
        <path d="M12 46 32 14l20 32Z" fill="none" stroke="var(--power)" strokeWidth="4" strokeLinejoin="round" />
        <circle cx="32" cy="36" r="5" fill="var(--coolant)" />
      </svg>
      <span className="text-[15px]">Glasshouse</span>
    </Link>
  )
}

function ThemeToggle() {
  const { theme, setTheme } = useApp()
  return (
    <button className="btn !px-2.5 !py-1.5" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}>
      {theme === 'dark' ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></svg>
      )}
    </button>
  )
}

/** Every route is in the "strata & keyline" paper world (see DESIGN.md). */
export function isStrataRoute(_path: string) {
  return true
}

/** The landing poster sits on oat paper; every other page reads on a lighter sheet ground. */
function groundFor(path: string) {
  return path === '/' ? 'poster' : 'sheet'
}

export function TopNav({ overlay = false, strata = false }: { overlay?: boolean; strata?: boolean }) {
  return (
    <header className={clsx('z-40 w-full', overlay ? 'pointer-events-none absolute inset-x-0 top-0' : 'sticky top-0 border-b hairline bg-[color-mix(in_srgb,var(--nav-bg,var(--bg-0))_92%,transparent)] backdrop-blur-md')}>
      <nav aria-label="Main" className="pointer-events-auto mx-auto flex h-14 select-none max-w-[1400px] items-center gap-6 px-4 sm:px-6">
        <Logo />
        <ul className="hidden items-center gap-1 md:flex">
          {NAV.map((n) => (
            <li key={n.to}>
              <NavLink to={n.to} className={({ isActive }) => clsx('rounded-full px-3 py-1.5 text-sm transition-colors', isActive ? 'bg-[var(--surface-strong)] text-text' : 'text-muted hover:text-text')}>
                {n.label}
              </NavLink>
            </li>
          ))}
        </ul>
        <div className="ml-auto flex items-center gap-2">
          {!strata && <ThemeToggle />}
          <MobileMenu />
        </div>
      </nav>
    </header>
  )
}

function MobileMenu() {
  const nav = useNavigate()
  return (
    <select aria-label="Go to section" className="glass rounded-full px-3 py-1.5 text-sm md:hidden" value="" onChange={(e) => e.target.value && nav(e.target.value)}>
      <option value="">Menu</option>
      {NAV.map((n) => <option key={n.to} value={n.to}>{n.label}</option>)}
      <option value="/sources">Sources</option>
      <option value="/methodology">Methodology</option>
    </select>
  )
}

export function Footer() {
  return (
    <footer className="mt-24 border-t hairline">
      <div className="mx-auto grid max-w-[1400px] gap-8 px-4 py-10 text-sm sm:px-6 md:grid-cols-4">
        <div className="space-y-2">
          <Logo />
          <p className="text-muted">An open, public-service ledger of U.S. data centers. Every figure sourced and labeled.</p>
        </div>
        <div>
          <div className="kicker mb-2">Explore</div>
          <ul className="space-y-1 text-muted">
            <li><Link to="/map">Atlas map</Link></li><li><Link to="/inside">Inside a data center</Link></li>
            <li><Link to="/inside/prompt">Follow one prompt</Link></li><li><Link to="/voices">Community voices</Link></li>
          </ul>
        </div>
        <div>
          <div className="kicker mb-2">Trust</div>
          <ul className="space-y-1 text-muted">
            <li><Link to="/sources">Sources & licenses</Link></li><li><Link to="/methodology">Methodology</Link></li>
            <li><Link to="/learn/how-we-work">How we work</Link></li><li><Link to="/about">About & privacy</Link></li>
          </ul>
        </div>
        <div className="text-xs text-faint">
          <p>Facility data: Compute Atlas, Data Center Index, Epoch AI (CC BY 4.0); OpenStreetMap contributors (ODbL). Context: EPA eGRID, EIA, U.S. Census, WRI Aqueduct 4.0 (CC BY 4.0), CDC/ATSDR EJI.</p>
          <p className="mt-2">AI: IBM Granite, Granite Guardian, Docling, Mellea, granite-embedding.</p>
        </div>
      </div>
    </footer>
  )
}

export function Layout() {
  const loc = useLocation()
  const fullscreen = loc.pathname.startsWith('/map') || loc.pathname === '/inside' || loc.pathname === '/inside/prompt'
  const overlayNav = fullscreen || loc.pathname === '/'
  const strata = isStrataRoute(loc.pathname)
  const ground = groundFor(loc.pathname)
  useEffect(() => {
    const root = document.documentElement
    if (strata) root.dataset.world = 'strata'
    else delete root.dataset.world
    root.dataset.ground = ground
  }, [strata, ground])
  useEffect(() => {
    if (!loc.hash) window.scrollTo({ top: 0 })
  }, [loc.pathname, loc.hash])
  return (
    <div className="min-h-full">
      <a href="#main" className="skip-link">Skip to content</a>
      <TopNav overlay={overlayNav} strata={strata} />
      <main id="main" tabIndex={-1} className="outline-none">
        <Suspense fallback={<div className="flex h-[60vh] items-center justify-center"><Spinner /></div>}>
          <Outlet />
        </Suspense>
      </main>
      {!fullscreen && <Footer />}
      <Suspense fallback={null}><GuideDock /></Suspense>
    </div>
  )
}
